// shared utilities (currentPeriodId used by accounting context)
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { Search } from "lucide-react";
import {
	type CashFlowSection,
	type ReportLine,
	Side,
} from "@/gen/omnios/v1/accounting_pb";
import {
	useGetBalanceSheet,
	useGetCashFlowStatement,
	useGetIncomeStatement,
	useGetTrialBalance,
	useListAccounts,
} from "@/hooks/use-accounting";
import {
	type DimensionFilter,
	DimensionFilterBar,
} from "@/components/accounting/dimension-filter-bar";
import { useAccountingContext } from "@/lib/accounting-context";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";

import type {
	CellClickedEvent,
	ColDef,
	ValueFormatterParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { DrillDownDialog } from "@/components/accounting/drill-down-dialog";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface StatementsSearch {
	tab?: ReportTab;
	expanded?: boolean;
	compare?: boolean;
}

export const Route = createFileRoute("/accounting/statements")({
	component: StatementsPage,
	validateSearch: (search: Record<string, unknown>): StatementsSearch => ({
		tab: (["osv", "opu", "balance", "dds"] as ReportTab[]).includes(
			search.tab as ReportTab,
		)
			? (search.tab as ReportTab)
			: undefined,
		expanded:
			search.expanded === true || search.expanded === "true" || undefined,
		compare: search.compare === true || search.compare === "true" || undefined,
	}),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function splitBalance(
	net: string,
	normalBalance: number,
): { debit: number; credit: number } {
	const v = Number.parseFloat(net || "0");
	if (Math.abs(v) < 0.005) return { debit: 0, credit: 0 };
	if (normalBalance === Side.DEBIT) {
		return v > 0 ? { debit: v, credit: 0 } : { debit: 0, credit: -v };
	}
	return v > 0 ? { debit: 0, credit: v } : { debit: -v, credit: 0 };
}

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

type ReportTab = "osv" | "opu" | "balance" | "dds";

/** Active-passive accounts that should show expanded (развёрнутое) balance */
const ACTIVE_PASSIVE_ACCOUNTS = new Set([
	"60",
	"62",
	"68",
	"69",
	"71",
	"73",
	"75",
	"76",
]);

function isActivePassive(code: string): boolean {
	const prefix = code.split(".")[0];
	return ACTIVE_PASSIVE_ACCOUNTS.has(prefix);
}

interface TrialBalanceRow {
	accountId: string;
	accountCode: string;
	accountName: string;
	normalBalance: number;
	openDebit: number;
	openCredit: number;
	turnoverDebit: number;
	turnoverCredit: number;
	closeDebit: number;
	closeCredit: number;
	isActivePassive: boolean;
	// Prior period comparison
	priorCloseDebit?: number;
	priorCloseCredit?: number;
	changeDebit?: number;
	changeCredit?: number;
}

interface StatementRow {
	code: string;
	name: string;
	amount: number;
	indent: number;
	isTotal: boolean;
	section?: string;
	priorAmount?: number;
	change?: number;
}

// ---------------------------------------------------------------------------
// Tab button strip
// ---------------------------------------------------------------------------

const TABS: { id: ReportTab; labelKey: string }[] = [
	{ id: "osv", labelKey: "trial_balance" },
	{ id: "opu", labelKey: "income_statement" },
	{ id: "balance", labelKey: "balance_sheet" },
	{ id: "dds", labelKey: "cash_flow" },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function StatementsPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("statements");
	const search = Route.useSearch();

	const activeTab = search.tab ?? "osv";
	const expanded = search.expanded ?? false;
	const compare = search.compare ?? false;
	const { periodId, basis } = useAccountingContext();
	const [dimensionsFilter, setDimensionsFilter] = useState<DimensionFilter>({});

	const setActiveTab = (tab: ReportTab) =>
		navigate({
			to: "/accounting/statements",
			search: { ...search, tab: tab === "osv" ? undefined : tab },
			replace: true,
		});
	const setExpanded = (v: boolean | ((prev: boolean) => boolean)) => {
		const val = typeof v === "function" ? v(expanded) : v;
		navigate({
			to: "/accounting/statements",
			search: { ...search, expanded: val || undefined },
			replace: true,
		});
	};
	const setCompare = (v: boolean | ((prev: boolean) => boolean)) => {
		const val = typeof v === "function" ? v(compare) : v;
		navigate({
			to: "/accounting/statements",
			search: { ...search, compare: val || undefined },
			replace: true,
		});
	};

	// Compute prior period ID (previous month)
	const priorPeriodId = useMemo(() => {
		if (!periodId) return "";
		const [y, m] = periodId.split("-").map(Number);
		if (!y || !m) return "";
		const pm = m === 1 ? 12 : m - 1;
		const py = m === 1 ? y - 1 : y;
		return `${py}-${String(pm).padStart(2, "0")}`;
	}, [periodId]);

	// Fetch accounts for name lookup
	const { data: accountsData } = useListAccounts();
	const accountNameMap = useMemo(() => {
		const map = new Map<string, string>();
		for (const a of accountsData?.accounts ?? []) {
			map.set(a.code, a.name);
		}
		return map;
	}, [accountsData]);

	// Fetch all report data — current and prior period
	const { data: tbData, isLoading: tbLoading } = useGetTrialBalance(
		periodId,
		basis,
	);
	const { data: tbPrior } = useGetTrialBalance(
		compare ? priorPeriodId : "",
		basis,
	);
	const { data: isData, isLoading } = useGetIncomeStatement(periodId, basis);
	const { data: isPrior } = useGetIncomeStatement(compare ? priorPeriodId : "", basis);
	const { data: bsData, isLoading: bsLoading } = useGetBalanceSheet(
		periodId,
		basis,
	);
	const { data: bsPrior } = useGetBalanceSheet(compare ? priorPeriodId : "", basis);
	const { data: cfData, isLoading: cfLoading } = useGetCashFlowStatement(
		periodId,
		basis,
	);
	const { data: cfPrior } = useGetCashFlowStatement(compare ? priorPeriodId : "", basis);

	// ── Trial balance rows ──
	const tbRows = useMemo<TrialBalanceRow[]>(() => {
		if (!tbData) return [];
		return tbData.lines.map((line) => {
			const turnoverDebit = Number.parseFloat(line.totalDebits || "0");
			const turnoverCredit = Number.parseFloat(line.totalCredits || "0");
			const netVal = Number.parseFloat(line.net || "0");
			const openingNet = netVal - (turnoverDebit - turnoverCredit);
			const ap = isActivePassive(line.accountCode);

			let opening: { debit: number; credit: number };
			let closing: { debit: number; credit: number };

			if (expanded && ap) {
				// Развёрнутое сальдо: show both debit and credit balances
				// For active-passive accounts, debits and credits represent
				// different economic realities (e.g., receivables vs advances)
				opening = {
					debit: Math.max(0, openingNet),
					credit: Math.max(0, -openingNet),
				};
				closing = {
					debit: Math.max(0, netVal),
					credit: Math.max(0, -netVal),
				};
			} else {
				opening = splitBalance(String(openingNet), line.normalBalance);
				closing = splitBalance(line.net, line.normalBalance);
			}

			// Prior period comparison
			let priorCloseDebit: number | undefined;
			let priorCloseCredit: number | undefined;
			if (compare && tbPrior) {
				const priorLine = tbPrior.lines.find(
					(pl) => pl.accountCode === line.accountCode,
				);
				if (priorLine) {
					const priorClosing = splitBalance(
						priorLine.net,
						priorLine.normalBalance,
					);
					priorCloseDebit = priorClosing.debit;
					priorCloseCredit = priorClosing.credit;
				}
			}

			return {
				accountId: line.accountCode,
				accountCode: line.accountCode,
				accountName: accountNameMap.get(line.accountCode) ?? "",
				normalBalance: line.normalBalance,
				openDebit: opening.debit,
				openCredit: opening.credit,
				turnoverDebit,
				turnoverCredit,
				closeDebit: closing.debit,
				closeCredit: closing.credit,
				isActivePassive: ap,
				priorCloseDebit,
				priorCloseCredit,
				changeDebit:
					priorCloseDebit != null ? closing.debit - priorCloseDebit : undefined,
				changeCredit:
					priorCloseCredit != null
						? closing.credit - priorCloseCredit
						: undefined,
			};
		});
	}, [tbData, tbPrior, expanded, compare, accountNameMap]);

	const tbPinned = useMemo(() => {
		if (tbRows.length === 0) return [];
		const totals: TrialBalanceRow = {
			accountId: "",
			accountCode: t("total"),
			accountName: "",
			normalBalance: 0,
			openDebit: 0,
			openCredit: 0,
			turnoverDebit: Number.parseFloat(tbData?.totalDebits || "0"),
			turnoverCredit: Number.parseFloat(tbData?.totalCredits || "0"),
			closeDebit: 0,
			closeCredit: 0,
			isActivePassive: false,
		};
		for (const r of tbRows) {
			totals.openDebit += r.openDebit;
			totals.openCredit += r.openCredit;
			totals.closeDebit += r.closeDebit;
			totals.closeCredit += r.closeCredit;
		}
		return [totals];
	}, [tbRows, tbData, t]);

	// ── Statement rows (P&L, BS, CF) ──
	function flattenLines(lines: ReportLine[]): StatementRow[] {
		return lines.map((l) => ({
			code: l.code,
			name: l.name,
			amount: Number.parseFloat(l.amount || "0"),
			indent: l.indent,
			isTotal: l.isTotal,
		}));
	}

	function flattenCashFlow(cf: typeof cfData): StatementRow[] {
		if (!cf) return [];
		const rows: StatementRow[] = [];
		const addSection = (
			section: CashFlowSection | undefined,
			label: string,
		) => {
			if (!section) return;
			rows.push({
				code: "",
				name: label,
				amount: 0,
				indent: 0,
				isTotal: true,
				section: label,
			});
			for (const l of section.lines) {
				rows.push({
					code: l.code,
					name: l.name,
					amount: Number.parseFloat(l.amount || "0"),
					indent: l.indent + 1,
					isTotal: l.isTotal,
				});
			}
			rows.push({
				code: "",
				name: t("subtotal"),
				amount: Number.parseFloat(section.subtotal || "0"),
				indent: 0,
				isTotal: true,
			});
		};
		addSection(cf.operating, t("section_operating"));
		addSection(cf.investing, t("section_investing"));
		addSection(cf.financing, t("section_financing"));
		return rows;
	}

	const stmtRows = useMemo<StatementRow[]>(() => {
		let current: StatementRow[] = [];
		let prior: StatementRow[] = [];
		if (activeTab === "opu") {
			current = flattenLines(isData?.lines ?? []);
			if (compare) prior = flattenLines(isPrior?.lines ?? []);
		} else if (activeTab === "balance") {
			current = flattenLines(bsData?.lines ?? []);
			if (compare) prior = flattenLines(bsPrior?.lines ?? []);
		} else if (activeTab === "dds") {
			current = flattenCashFlow(cfData);
			if (compare) prior = flattenCashFlow(cfPrior);
		}
		if (!compare || prior.length === 0) return current;

		// Merge prior amounts by matching code+name
		const priorMap = new Map<string, number>();
		for (const p of prior) {
			priorMap.set(`${p.code}:${p.name}`, p.amount);
		}
		return current.map((row) => {
			const priorAmount = priorMap.get(`${row.code}:${row.name}`) ?? 0;
			return {
				...row,
				priorAmount,
				change: row.amount - priorAmount,
			};
		});
	}, [activeTab, isData, isPrior, bsData, bsPrior, cfData, cfPrior, compare, t]);

	const stmtPinned = useMemo<StatementRow[]>(() => {
		if (stmtRows.length === 0) return [];
		let total = "0";
		if (activeTab === "opu") total = isData?.total ?? "0";
		else if (activeTab === "balance") total = bsData?.total ?? "0";
		else if (activeTab === "dds") total = cfData?.netChange ?? "0";
		return [
			{
				code: "",
				name: t("total"),
				amount: Number.parseFloat(total),
				indent: 0,
				isTotal: true,
			},
		];
	}, [stmtRows.length, activeTab, isData, bsData, cfData, t]);

	// ── Column defs ──
	const amountFmt = (p: ValueFormatterParams) =>
		p.value ? formatAmount(p.value) : "";

	const drillCellClass =
		"font-tabular cursor-pointer hover:underline text-primary";

	const tbColDefs = useMemo<ColDef<TrialBalanceRow>[]>(
		() => [
			{
				field: "accountCode",
				headerName: t("account_code"),
				width: 70,
				pinned: "left" as const,
				cellClass: "font-mono cursor-pointer hover:underline text-primary",
			},
			{
				field: "accountName",
				headerName: t("account_name"),
				flex: 2,
				minWidth: 140,
			},
			{
				field: "openDebit",
				headerName: `${t("opening_balance_short")} Дт`,
				flex: 1,
				minWidth: 90,
				type: "rightAligned",
				cellClass: drillCellClass,
				valueFormatter: amountFmt,
			},
			{
				field: "openCredit",
				headerName: `${t("opening_balance_short")} Кт`,
				flex: 1,
				minWidth: 90,
				type: "rightAligned",
				cellClass: drillCellClass,
				valueFormatter: amountFmt,
			},
			{
				field: "turnoverDebit",
				headerName: `${t("turnover")} Дт`,
				flex: 1,
				minWidth: 90,
				type: "rightAligned",
				cellClass: drillCellClass,
				valueFormatter: amountFmt,
			},
			{
				field: "turnoverCredit",
				headerName: `${t("turnover")} Кт`,
				flex: 1,
				minWidth: 90,
				type: "rightAligned",
				cellClass: drillCellClass,
				valueFormatter: amountFmt,
			},
			{
				field: "closeDebit",
				headerName: `${t("closing_balance_short")} Дт`,
				flex: 1,
				minWidth: 90,
				type: "rightAligned",
				cellClass: drillCellClass,
				valueFormatter: amountFmt,
			},
			{
				field: "closeCredit",
				headerName: `${t("closing_balance_short")} Кт`,
				flex: 1,
				minWidth: 90,
				type: "rightAligned",
				cellClass: drillCellClass,
				valueFormatter: amountFmt,
			},
			// Comparison columns — only visible when compare is on
			...((compare
				? [
						{
							field: "priorCloseDebit" as const,
							headerName: `${priorPeriodId} ${t("line_debit")}`,
							width: 110,
							type: "rightAligned" as const,
							cellClass: "font-tabular text-muted-foreground",
							valueFormatter: amountFmt,
						},
						{
							field: "priorCloseCredit" as const,
							headerName: `${priorPeriodId} ${t("line_credit")}`,
							width: 110,
							type: "rightAligned" as const,
							cellClass: "font-tabular text-muted-foreground",
							valueFormatter: amountFmt,
						},
						{
							field: "changeDebit" as const,
							headerName: `Δ ${t("line_debit")}`,
							width: 100,
							type: "rightAligned" as const,
							cellClass: "font-tabular",
							valueFormatter: amountFmt,
							cellStyle: (p: { value: number | null }) => ({
								color:
									p.value != null && p.value > 0
										? "hsl(var(--chart-2))"
										: p.value != null && p.value < 0
											? "hsl(var(--destructive))"
											: undefined,
							}),
						},
						{
							field: "changeCredit" as const,
							headerName: `Δ ${t("line_credit")}`,
							width: 100,
							type: "rightAligned" as const,
							cellClass: "font-tabular",
							valueFormatter: amountFmt,
							cellStyle: (p: { value: number | null }) => ({
								color:
									p.value != null && p.value > 0
										? "hsl(var(--chart-2))"
										: p.value != null && p.value < 0
											? "hsl(var(--destructive))"
											: undefined,
							}),
						},
					]
				: []) as ColDef<TrialBalanceRow>[]),
		],
		[t, compare, priorPeriodId],
	);

	const stmtColDefs = useMemo<ColDef<StatementRow>[]>(
		() => [
			{
				field: "name",
				headerName: t("account_name"),
				flex: 1,
				minWidth: 300,
				cellStyle: (p) => {
					const row = p.data as StatementRow | undefined;
					if (!row) return {};
					const style: Record<string, string> = {
						paddingLeft: `${8 + row.indent * 20}px`,
					};
					if (row.isTotal) style.fontWeight = "700";
					return style;
				},
			},
			{
				field: "amount",
				headerName: compare ? `${periodId}` : t("line_amount"),
				width: 180,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueFormatter: amountFmt,
				cellStyle: (p) => {
					const row = p.data as StatementRow | undefined;
					return row?.isTotal ? { fontWeight: "700" } : null;
				},
			},
			...(compare
				? [
						{
							field: "priorAmount" as const,
							headerName: priorPeriodId,
							width: 160,
							type: "rightAligned" as const,
							cellClass: "font-tabular text-muted-foreground",
							valueFormatter: amountFmt,
							cellStyle: (p: { data: StatementRow | undefined }) =>
								p.data?.isTotal ? { fontWeight: "700" } : null,
						},
						{
							field: "change" as const,
							headerName: "Δ",
							width: 140,
							type: "rightAligned" as const,
							cellClass: "font-tabular",
							valueFormatter: amountFmt,
							cellStyle: (p: { value: number | null; data: StatementRow | undefined }) => {
								const style: Record<string, string> = {};
								if (p.data?.isTotal) style.fontWeight = "700";
								if (p.value != null && p.value > 0) style.color = "hsl(var(--chart-2))";
								else if (p.value != null && p.value < 0) style.color = "hsl(var(--destructive))";
								return style;
							},
						},
					]
				: []) as ColDef<StatementRow>[],
		],
		[t, compare, periodId, priorPeriodId],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	// ── Drill-down ──
	const [drillDown, setDrillDown] = useState<{
		accountCode: string;
		title: string;
	} | null>(null);

	const onTbCellClicked = useCallback(
		(e: CellClickedEvent<TrialBalanceRow>) => {
			if (!e.data || e.data.accountCode === t("total")) return;
			const field = e.colDef.field;
			// Click on account code → navigate to account card
			if (field === "accountCode" || field === "accountName") {
				const accountId = e.data.accountId || e.data.accountCode;
				navigate({
					to: "/accounting/accounts/$id",
					params: { id: accountId },
				});
				return;
			}
			// Click on any amount → drill-down dialog
			if (e.value != null && e.value !== 0) {
				setDrillDown({
					accountCode: e.data.accountCode,
					title: `${e.data.accountCode} ${e.data.accountName} — ${e.colDef.headerName ?? ""}`,
				});
			}
		},
		[navigate, t],
	);

	// ── Active loading state ──
	const loading =
		activeTab === "osv"
			? tbLoading
			: activeTab === "opu"
				? isLoading
				: activeTab === "balance"
					? bsLoading
					: cfLoading;

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			},
			{
				id: "basis",
				content: `${t("basis")}: ${basis.toUpperCase()}`,
				align: "left" as const,
			},
		],
		[periodId, basis, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	// ── Render ──
	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				{TABS.map((tab) => (
					<Button
						key={tab.id}
						variant={activeTab === tab.id ? "default" : "outline"}
						size="sm"
						className="h-9"
						onClick={() => setActiveTab(tab.id)}
					>
						{t(tab.labelKey)}
					</Button>
				))}
				{activeTab === "osv" && (
					<Button
						variant={expanded ? "default" : "outline"}
						size="sm"
						className="h-9"
						onClick={() => setExpanded((v) => !v)}
					>
						{t("expanded_balance")}
					</Button>
				)}
				<Button
					variant={compare ? "default" : "outline"}
					size="sm"
					className="h-9"
					onClick={() => setCompare((v) => !v)}
				>
					{t("compare_periods", { defaultValue: "Сравнить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => window.print()}
				>
					{t("toolbar_print")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `${activeTab}_${periodId}.csv`,
						})
					}
				>
					CSV
				</Button>
			</div>

			{/* Dimension filter bar */}
			<DimensionFilterBar value={dimensionsFilter} onChange={setDimensionsFilter} />

			{/* Drill-down dialog */}
			<DrillDownDialog
				open={!!drillDown}
				onOpenChange={(open) => { if (!open) setDrillDown(null); }}
				accountCode={drillDown?.accountCode}
				periodId={periodId}
				title={drillDown?.title}
			/>

			{/* Grid — fills all space, toolbar handles report tabs + filters */}
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				{activeTab === "osv" ? (
					<AgGridReact<TrialBalanceRow>
						ref={gridRef as React.RefObject<AgGridReact<TrialBalanceRow>>}
						rowData={tbRows}
						columnDefs={tbColDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						onGridReady={onGridReady}
						onSortChanged={onStateChanged}
						onFilterChanged={onStateChanged}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						rowHeight={28}
						headerHeight={32}
						animateRows={false}
						getRowId={(p) => p.data.accountCode}
						loading={loading}
						pinnedBottomRowData={tbPinned}
						onCellClicked={onTbCellClicked}
						overlayNoRowsTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("no_data_for_period")}</span>`}
					/>
				) : (
					<AgGridReact<StatementRow>
						ref={gridRef as React.RefObject<AgGridReact<StatementRow>>}
						rowData={stmtRows}
						columnDefs={stmtColDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						onGridReady={onGridReady}
						onSortChanged={onStateChanged}
						onFilterChanged={onStateChanged}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						rowHeight={28}
						headerHeight={32}
						animateRows={false}
						getRowId={(p) =>
							`${p.data.section ?? ""}:${p.data.code}:${p.data.name}:${p.data.indent}`
						}
						loading={loading}
						pinnedBottomRowData={stmtPinned}
						overlayNoRowsTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("no_data_for_period")}</span>`}
					/>
				)}
			</div>
		</div>
	);
}
